'use strict';

/**
* @ngdoc service
* @name tcApp.WebsysLegacyScope
* @description
* General websys/global functions.
* Contains MEUI equivalents for legacy functions originally defined globally or in websys.js
* Also includes some functions only used in MEUI
*/
angular.module('tc.legacyScope')
.factory('WebsysLegacyScope', function($window, $log, $location, $routeParams, $timeout, $interval, $rootScope,
										$httpParamSerializer, GlobalService, WorkflowService, ComponentCache,
										BreadcrumbService, LegacyScopeSearchService, LegacyScopeUtilsService,
										OverlayService, ToastService, tcConstants) {

	/* eslint no-unused-vars:0 */
	return function($scope, legacyScopeObject) {
		// Global functions

		var emptyFunction = function emptyFunction() {
		};

		var buttonClickHandler = function() {
			$scope.viewModel.processEventOptions.checkReturnValue = false;
		};

		var linkReturnHandler = function() {
			return true;
		};

		/**
		* @ngdoc method
		* @name tkMakeServerCall
		* @methodOf tcApp.WebsysLegacyScope
		* @description
		* Executes a synchronous server call to a specified IRIS class method with parameters
		* @param {string | object} classNameOrOptions IRIS class name, or options object
		* @param {string} classNameOrOptions.className IRIS class name
		* @param {string} classNameOrOptions.methodName IRIS method name
		* @param {string[] | string | undefined} classNameOrOptions.args One or more arguments passed to the class method
		*	- can be a single argument, or an array of one or more arguments
		* @param {string=} classNameOrOptions.confirmAction The confirmAction to use if the class method returns a W or I type message
		* @param {string=} method IRIS method name; used only if classNameOrOptions is not an object
		* @param {...string} args One or more arguments passed to the class method; used only if classNameOrOptions is not an object
		* @returns {string} Return value from the IRIS class method
		* NOTE: This method is duplicated in menuCheckService.js, albeit with simplified behavior and error handling;
		*   when making changes here, also double check if those changes need to be duplicated in menuCheckService.js
		*/
		var tkMakeServerCall = function(classNameOrOptions, method) {
			var args;
			var confirmAction = '';
			if (angular.isObject(classNameOrOptions)) {
				if (classNameOrOptions.className) {
					var className = classNameOrOptions.className;
				} else {
					$log.error($scope.componentName, ': tkMakeServerCall: className is required');
					return '<TC.ERROR>';
				}
				if (classNameOrOptions.methodName) {
					method = classNameOrOptions.methodName;
				} else {
					$log.error($scope.componentName, ': tkMakeServerCall: method is required');
					return '<TC.ERROR>';
				}
				if (angular.isDefined(classNameOrOptions.args)) {
					if (angular.isArray(classNameOrOptions.args)) {
						args = classNameOrOptions.args;
					} else {
						args = [classNameOrOptions.args];
					}
				} else {
					args = [];
				}
				args.unshift(className, method); // add classname and method to the beginning of the args, for use with function.apply()
				if (angular.isDefined(classNameOrOptions.confirmAction)) confirmAction = classNameOrOptions.confirmAction;
			} else {
				args = Array.prototype.slice.apply(arguments);
			}
			var override = false;
			// call is also used from ckEditor trakcare plugins where viewModel does not exist
			if ((angular.isDefined($scope.viewModel)) && (angular.isDefined($scope.viewModel.processEventOptions))) {
				override = $scope.viewModel.processEventOptions.overrideWarning;
			$scope.viewModel.processEventOptions.overrideWarning = false; // clear override so the component doesn't get stuck overriding on every call
			}
			args.unshift(GlobalService.serverMethods[0], 'tkMakeServerCall' + (override ? ':override' : ''));
			var returnValue = $window.cspHttpServerMethod.apply(this, args);
			if (returnValue === '<TC.ERROR>') {
				$log.error($scope.componentName, ': tkMakeServerCall:', $scope.globalStrings['XERROR']);
			} else {
				try {
					var returnObj = angular.fromJson(returnValue);
					if (angular.isArray(returnObj.messages) && (returnObj.messages.length > 0)) {
						var allowedMessageTypes = [tcConstants.COMPONENT_MESSAGE_TYPE.ERROR];
						if (confirmAction && setConfirmAction(confirmAction, 'tkMakeServerCall')) {
							// without a valid confirmAction, only 'E' type messages are supported
							allowedMessageTypes.push(
								tcConstants.COMPONENT_MESSAGE_TYPE.WARNING,
								tcConstants.COMPONENT_MESSAGE_TYPE.INFO
							);
						}
						var allowedMessages = returnObj.messages.filter(function(message) {
							return allowedMessageTypes.includes(message.messageType);
						});
						if (allowedMessages.length < returnObj.messages.length) {
							var disallowedMessages = returnObj.messages.filter(function(message) {
								return (!allowedMessageTypes.includes(message.messageType));
							});
							$log.warn(
								'Some server side messages returned by tkMakeServerCall were not displayed:',
								disallowedMessages
							);
						}
						if (allowedMessages.length > 0) $scope.updateMessages(allowedMessages, undefined, undefined, false, true);
					}
					if (angular.isArray(returnObj.toasts)) GlobalService.displayToasts(returnObj.toasts);
					if (angular.isDefined(returnObj.returnValue)) {
						return returnObj.returnValue;
					} else {
						return '';
					}
				} catch (ex) {
					$log.error('Error parsing tkMakeServerCall response:', ex);
				}
			}
			return returnValue;
		};
		
		var cspRunServerMethod = function(method) {
			var errorMessage;
			if ($window.cspFindXMLHttp(false) == null) {
				errorMessage = 'Unable to locate XMLHttpObject.';
				if (typeof $window.cspRunServerMethodError == 'function') {
					return $window.cspRunServerMethodError(errorMessage);
				} else {
					$window.alert(errorMessage);
					return null;
				}
			}
			return $window.cspIntHttpServerMethod(method, arguments, false);
		};

		/**
		* @ngdoc method
		* @name websys_fetch
		* @methodOf tcApp.WebsysLegacyScope
		* @description
		* Executes an asynchronous server call to a specified IRIS class method with parameters
		* Returns a Promise that is resolved or rejected
		* @param {object} options object with properties:
		* @param {string} options.methodName IRIS method name that is defined in the component JS class.
		*	Method must be white listed in the JS class, and in most cases should be defined there
		* @param {any[] | any} options.args Array of arguments to pass to class method. Single non-array value will be wrapped in an array
		* @param {string=} options.className [Optional] IRIS class name. Only used where the method is not defined in the component JS class.
		*	Must be a JS class extending websys.AbstractJavaScript
		* }
		* @returns {Promise=} Promise that resolves, passing the return value from the IRIS class method to a function.
		*	Returns undefined if the browser does not support Promises.
		*/
		var websysFetch = function(options) {
			if ((!angular.isFunction($window.fetch)) || (!angular.isFunction($window.Promise))) {
				$log.warn($scope.componentName, ': websys_fetch: Not supported in this browser');
				return;
			}
			var errorMessage;
			options = (options || {});
			if ((angular.isUndefined(options.methodName)) || (options.methodName === '')) {
				errorMessage = ($scope.componentName + ': websys_fetch: Method name is required');
				$log.error(errorMessage);
				return $window.Promise.reject(Error(errorMessage));
			}
			if (angular.isUndefined(options.className)) options.className = '';
			if (options.className.substr(0, 1) === '%') {
				errorMessage = ($scope.componentName + ': websys_fetch: Class name is invalid');
				$log.error(errorMessage);
				return $window.Promise.reject(Error(errorMessage));
			}
			try {
				var serverMethod = GlobalService.serverMethods[0];
				if (angular.isDefined(serverMethod)) {
					var wargCount = 3;
					var parameters = [
						'WEVENT=' + serverMethod,
						'WARG_1=' + encodeURIComponent($scope.componentName),
						'WARG_2=' + encodeURIComponent(options.className),
						'WARG_3=' + encodeURIComponent(options.methodName)
					];
					if (angular.isDefined(options.args)) {
						var args;
						// Allow a single argument to be passed without using an array
						if (!angular.isArray(options.args)) {
							args = [options.args];
						} else {
							args = options.args.slice();
						}
						var wargIndex = 4;
						angular.forEach(args, function(arg) {
							// Convert boolean to ObjectScript value before sending
							if (typeof arg === 'boolean') {
								arg = (arg ? 1 : 0);
							} else {
								arg = arg.toString();
							}
							parameters.push('WARG_' + (wargIndex++) + '=' + encodeURIComponent(arg));
							wargCount++;
						});
					}
					parameters.splice(0, 0, ('WARGC=' + wargCount));
					parameters = parameters.join('&');
					return $window.fetch('%25CSP.Broker.cls', {
						method: 'post',
						mode: 'same-origin',
						credentials: 'same-origin',
						cache: 'no-store',
						redirect: 'error',
						keepalive: true,
						headers: {
							'Content-type': 'application/x-www-form-urlencoded; charset=UTF-8'
						},
						body: parameters
					}).then(function(response) {
						if (response.status !== 200) {
							$log.error($scope.componentName, ': websys_fetch: Returned error. Status:',
										response.status, response.statusText);
							return $window.Promise.reject(Error(response.status + '\n' + response.statusText));
						}
						// Process the text in the response
						return response.text().then(function(data) {
							var responseProcessed = processFetchReponse(data);
							if (responseProcessed.status !== null) {
								$log.error($scope.componentName, ': websys_fetch: Server error:', responseProcessed.status);
								return $window.Promise.reject(Error(responseProcessed.status));
							} else {
								try {
									var returnObj = angular.fromJson(responseProcessed.data);
									if (angular.isArray(returnObj.messages)) {
										// ignore messages object for async server calls
										if (returnObj.messages.length > 0) {
											$log.warn(
												'Server side messages returned by websys_fetch were not displayed:',
												returnObj.messages
											);
										}
									}
									if (angular.isArray(returnObj.toasts)) GlobalService.displayToasts(returnObj.toasts);
									if (angular.isDefined(returnObj.returnValue)) {
										return returnObj.returnValue;
									} else {
										return '';
									}
								} catch (ex) {
									$log.error('Error parsing websys_fetch response:', ex);
								}
								return responseProcessed.data;
							}
						}, function(error) {
							$log.error($scope.componentName, ': websys_fetch: Error processing response text:', error);
							return $window.Promise.reject(Error(error.message));
						});
					}, function(error) {
						$log.error($scope.componentName, ': websys_fetch: Error on fetching data:', error);
						return $window.Promise.reject(Error(error.message));
					});
				}
			} catch (error) {
				$log.error($scope.componentName, ': websys_fetch: Error:', error);
				return $window.Promise.reject(Error(error.message));
			}
		};

		// Processes the response text returned by the IRIS server call
		// (See also: CSP broker cspxmlhttp.js and TC UI tklib.js)
		var processFetchReponse = function(responseData) {
			var response = {
				status: null,
				data: ''
			};
			var i;
			var lines = responseData.split('\r\n');
			var ok = 0;
			var len = lines.length;
			if (lines[len - 1] === '') len = len - 1;
			for (i = 2; i < len; i++) {
				if (lines[i] === '#OK') {
					ok = i;
					break;
				}
			}

			if ((ok == 0) || ((lines[1] !== '#R') && (lines[1] !== '#V'))) {
				response.status = ('Http object response incomplete or invalid.' + ok + ',' + lines[1]);
				return response;
			}
			var result = '';
			if (lines[1] == '#R') {
				result = '';
				if (ok + 1 < len) {
					result = lines[ok + 1];
					for (i = ok + 2; i < len; i++) {
						result = result + '\r\n' + lines[i];
					}
				}
			}
			if (result === '<TC.ERROR>') {
				response.status = $scope.globalStrings['XERROR'];
			} else {
				response.data = result;
			}

			// Detect if the CSP broker returned some JS to execute
			var js = '';
			var alertText;
			if (ok > 2) {
				js = lines[2];
				for (i = 3; i < ok; i++) {
					js = js + '\r\n' + lines[i];
					if (lines[i].indexOf('alert(') === 0) {
						alertText = lines[i].split('alert(')[1];
						if (alertText) alertText = alertText.substr(0, alertText.lastIndexOf(');'));
						if (response.status !== null) {
							response.status += alertText;
						} else {
							response.status = alertText;
						}
					}
				}
			}

			return response;
		};

		var standardOnLoad = function(fieldArray) {
			// From codetables.js
			if ($scope.data.items) {
				for (var key in fieldArray) {
					if ($scope.data.items[fieldArray[key]]) $scope.data.items[fieldArray[key]].required = true;
				}
			}
		};

		var requiredMsgObj = function(obj, isList) {
			var required = false;
			if (obj.disabled !== true) {
				required = (isList ? (obj.selectedIndex == -1) : (obj.value == ''));
			}
			if (required) {
				return ('\'' + $scope.data.translations[obj.id] + '\' ' + $scope.globalStrings.XMISSING);
			} else {
				return '';
			}
		};

		var mPiece = function(s1, sep, n) {
			var delimArray = s1.split(sep);
			if ((n <= delimArray.length - 1) && (n >= 0)) return delimArray[n];
			return '';
		};

		// websys.js functions

		// Script that relies on user agent sniffing should not be required in MEUI
		var websysGetBrowser = function() {
			$log.warn($scope.componentName, ': Call to websys_getBrowser');
			return '';
		};

		//Script that relies on user agent sniffing should not be required in MEUI
		var websysGetBrowserVersion = function() {
			$log.warn($scope.componentName, ': Call to websys_getBrowserVersion');
			// Code calling this checks for lower versions
			return 100;
		};

		// Opens the url in the 'next level' of breadcrumbs
		// the unused are (,lookup,posn,params,) from websys_open
		var websysOpen = function(url, unusedLookup, unusedPosition, unusedParams, keepWindowOpen) {
			if ((url.indexOf('http') !== 0) && (url.indexOf('..') !== 0)
				&& (url.indexOf('#') !== 0) && (url.indexOf('/') !== 0)) {
				$log.warn('websys_open/websys_lu call with unconverted URL:', url);
			}
			// Open 3rd party links and external file formats in new window
			if ((url.indexOf('http') == 0) || (url.indexOf('..') == 0)
				|| (url.indexOf('websys.Document.cls?') === 0) || (url.indexOf('web.Msg.DocumentUpload.cls?') === 0)) {
				GlobalService.openWindow(url, '', '', keepWindowOpen);
				return false;
			}
			legacyScopeObject.windowObject.location = WorkflowService.getNextLevelUrl(url);
		};

		// Prints current page via browser
		var websysPrintMe = function() {
			$window.print();
		};

		// Returns the window object. MEUI only has one window
		var websysGetMainWindow = function() {
			$log.warn($scope.componentName, ': Call to websys_getMainWindow');
			return $window;
		};

		var websysGetMainFrame = function() {
			$log.warn($scope.componentName, ': Call to websys_getMainFrame');
			return null;
		};

		var websysLoadContent = function() {
			$log.warn($scope.componentName, ': Call to websys_LoadContent');
			return true;
		};

		var websysGetSrcElement = function(event) {
			// Change to return scope object?
			if (event) {
				if (event.target) {
					return event.target;
				} else {
					return event;
				}
			} else if ($window.event) {
				return $window.event.srcElement;
			}
		};

		var websysGetAncestorByTagName = function() {
			$log.warn($scope.componentName, ': Call to websys_getAncestorByTagName');
			return null;
		};
		var websysGetParentElement = function() {
			$log.warn($scope.componentName, ': Call to websys_getParentElement');
			return null;
		};
		var websysGetChildElements = function() {
			$log.warn($scope.componentName, ': Call to websys_getChildElements');
			return [];
		};
		var websysGetElementsByClassName = function() {
			$log.warn($scope.componentName, ': Call to websys_getElementsByClassName');
			return [];
		};

		var websysGetType = function(event) {
			if (event) return event.type;
			return null;
		};
		var websysGetKey = function(event) {
			if (event) {
				// 'which' gets set for mouse events as well as keyboard events
				var key = event.which;
				if ((key === 0) && (event.keyCode)) return event.keyCode;
				return key;
			}
		};
		var websysGetAlt = function(event) {
			if (event) return event.altKey;
			return null;
		};
		var websysGetShift = function(event) {
			if (event) return event.shiftKey;
			return null;
		};
		var websysGetCtrl = function(event) {
			if (event) return event.ctrlKey;
			return null;
		};

		var websysGetPageCoords = function() {
			$log.warn($scope.componentName, ': Call to websys_getPageCoords');
			return {x: 0, y: 0};
		};

		var websysCancel = function(event) {
			if (event instanceof Event) { // eslint-disable-line
				event.stopPropagation();
				event.preventDefault();
			}
			return false;
		};

		var websysIsDirty = function() {
			return $scope.componentForm[$scope.componentHtmlName].$dirty;
		};

		var websysSetDirty = function(isDirty) {
			if (angular.isUndefined($scope.componentForm[$scope.componentHtmlName])) {
				return;
			}
			if (isDirty) {
				$scope.componentForm[$scope.componentHtmlName].$setDirty();
			} else {
				$scope.componentForm[$scope.componentHtmlName].$setPristine();
			}
		};

		var tkurlhref = function(obj) {
			$log.warn($scope.componentName, ': Call to tkurlhref');
			return '';
		};
		var tkurlget = function() {
			$log.warn($scope.componentName, ': Call to tkurlget');
			return '';
		};
		/**
		* @ngdoc method
		* @name tkurlset
		* @methodOf tcApp.WebsysLegacyScope
		* @description
		* Server call that masks a URL's request parameters
		* @param {string} url URL with unmasked request parameters
		* @returns {string} Masked URL
		*/
		var tkurlset = function(url) {
			return GlobalService.maskUrl(url);
		};

		var websysCreateWindow = function(url) {
			if ((!url) || (url === '')) {
				$log.warn('websys_createWindow call with blank URL not supported');
				return;
			}
			if ((url.indexOf('http') !== 0) && (url.indexOf('..') !== 0)
				&& (url.indexOf('#') !== 0) && (url.indexOf('/') !== 0)) {
				$log.warn('websys_createWindow call with unconverted URL:', url);
			}
			// Mask the parameters if it is required
			if ((url.indexOf('http') !== 0) && (url.indexOf('..') !== 0)
				&& (url.indexOf('?') !== -1) && (url.indexOf('TUID=') === -1)
				&& (url.indexOf('websys.Document.cls?') !== 0)) {
				url = tkurlset(url);
			}
			// Open 3rd party links and external file formats in a new window (same as websys_open)
			if ((url.indexOf('http') == 0) || (url.indexOf('..') == 0) || (url.indexOf('websys.Document.cls?') === 0)) {
				GlobalService.openWindow(url);
				return;
			}

			url = $scope.openDialog(url);
			// We may need to set this - depending on whether we want the redirection to be handled by processEvent.
			// For JS functions on component menus/component item links, we currently depend on the javascript function
			// to return false
			//legacyScopeObject.windowObject.location = url;
		};

		/// some urls from code tables need to extend to fully formed external paths,
		///  or internal directories
		var websysGetFullExternalUrl = function(url) {
			// Support here urls and "help" all other "pass as is"
			var www=/www/gi;
			var http=/http:\/\/|https:\/\//gi;
			var htm=/htm/gi;
			var dir=/\\|file:\/\//gi;

			if (url.search(http) != -1) {
				// full url
			} else {
				if ((url.search(www) != -1) && ($window.location)) {
					// contains "www" means url --> add the "current" protocol
					url = $window.location.protocol+'//'+url;
				} else {
					// special for InternalFiles (PatientOrderFile, ObservationRefGuide, etc)
					// the name contains "htm" BUT is not a URI/URL or directory
					// --> this is a link to the ../Help/<url.htm(l)>
					if ((url.search(htm) != -1) && (url.search(dir) == -1)) {
						url='..\\Help\\'+url;
					}
				}
			}
			return url;
		};

		/// Open webcommon.Report.Custom component
		var openReportParamScreen  = function(linkUrlParams) {
			if (linkUrlParams != '') {
				if (linkUrlParams.indexOf('TREPORT') == -1) return false;
				websysCreateWindow('#/Component/webcommon.Report.Custom?' + linkUrlParams);
				return true;
			} else {
				return false;
			}
		};

		var websysPrint = function(printOptions, linkUrl, params, target, javascriptFunction, event, printLocalParams,extractType) {
			// First get the report code passed from system.Menu
			// Note that these will be overidden by the javascript function if they use the same key

			var linkUrlParams = linkUrl.split('?')[1];
			var requestParams = GlobalService.urlToObject(linkUrlParams);
			var javascriptRequestParams;
			var hiddenFrame;
			var localUrl;
			var actualPrintCase = '';
			var printCases = {PRINTVIAOVERLAY: 0,LOCALPRINTING: 1,PRINTVIASERVER: 2,OPENPARAMCOMP: 3};

			// Run logic in javascript function and get returned parameters
			if (angular.isString(javascriptFunction)
				&& (javascriptFunction !== '') && (angular.isFunction($scope[javascriptFunction]))) {
				if (angular.isDefined($scope.data.listRows)) {
					javascriptRequestParams = $scope[javascriptFunction].call(this, 't' + $scope.componentHtmlName, linkUrl, target);
				} else {
					javascriptRequestParams = $scope[javascriptFunction].call(this, linkUrl, target);
				}
				// Javascript function must use 'websys_printGetRequestParams' and return parameters
				if (angular.isObject(javascriptRequestParams)) {
					angular.extend(requestParams, javascriptRequestParams);
				} else {
					$log.warn('Printing failed: Javascript function "' + javascriptFunction + '" did not return request parameters');
					return false;
				}
			}

			/// Determine type of print logic scenario needed
			if ($scope.componentName == 'webcommon.Report.Custom') {
				if (printOptions == 1) {
					actualPrintCase = printCases.PRINTVIAOVERLAY;
				} else {
					if (angular.isObject(printLocalParams)) {
						actualPrintCase = printCases.LOCALPRINTING;
					} else {
						actualPrintCase = printCases.PRINTVIASERVER;
					}
				}
			} else if ($scope.componentName == 'websys.PrintOptions.Edit') {
				actualPrintCase = printCases.PRINTVIASERVER;
			} else {
				if (angular.isObject(printLocalParams)) {
					if (angular.isObject(requestParams) && (requestParams['WEBSYS.TCOMPONENT'] === 'webcommon.Report.Custom')) {
						actualPrintCase = printCases.OPENPARAMCOMP;
					} else {
						actualPrintCase = printCases.LOCALPRINTING;
					}
				} else {
					if (angular.isString(linkUrl) && (linkUrl.startsWith(tcConstants.PRINT_URL.AUTO))) {
						if (printOptions == 1) {
							actualPrintCase = printCases.PRINTVIAOVERLAY;
						} else {
							actualPrintCase = printCases.PRINTVIASERVER;
						}
					} else {
						actualPrintCase = printCases.OPENPARAMCOMP;
					}
				}
			}

			/// Do actual print scenarios
			switch (actualPrintCase) {
				case printCases.LOCALPRINTING:
					if ((angular.isUndefined(printLocalParams.localUrl)) || (printLocalParams.localUrl === '')) {
						localUrl = '../csp/websys.localprinting.frames.csp?PrintServer=';
						localUrl += printLocalParams.printMode + printLocalParams.params;
					} else {
						localUrl = printLocalParams.localUrl;
					}
					hiddenFrame = $window.document.getElementById('TRAK_hidden');
					delayPrintUsingFrame(hiddenFrame, GlobalService.getGlobalStrings().XPRINTINGLOCAL, localUrl, 100);
					break;
				case printCases.PRINTVIASERVER:
					if (angular.isObject(requestParams)) {
						$log.debug('Printing with parameters: ', requestParams);
						if ((angular.isString(extractType)) && (extractType === 'CSV')) {
							websysDisplayNotification(GlobalService.getGlobalStrings().XEXTRACTCSV);
						} else {
							websysDisplayNotification(GlobalService.getGlobalStrings().XPRINTING);	
						}						
						GlobalService.printViaServer(requestParams);
					} else {
						$log.warn('Printing failed: No request parameters');
					}
					break;
				case printCases.OPENPARAMCOMP:
					if (!openReportParamScreen(linkUrlParams)) {
						$log.warn('Printing failed: No request parameters');
					}
					break;
				case printCases.PRINTVIAOVERLAY:
					// requestParams AFTER Overlay are build above from URL (not using javascript again)
					// ==> the former build parameter via javascript are "lost"
					// append all javascriptParams to url
					if (angular.isObject(javascriptRequestParams)) {
						linkUrl += '&';
						linkUrl += Object.keys(javascriptRequestParams).map(function(key) {
								return encodeURIComponent(key) + '=' + encodeURIComponent(javascriptRequestParams[key]);
							}).join('&');
					}
					var optionsParams = '&CONTEXT=' + $scope.context + '&url=' +  encodeURIComponent(linkUrl) + '&params=' + params;
					if (angular.isDefined($scope.data.listRows)) optionsParams += '&tablename=t' + $scope.componentHtmlName;

					// the below options from TCUI websys.js is not required as it is used upon 'print' to pass to the jsFunction,
					//  which MEUI calls before the displaying PrintOptions
					//optionsParams += '&printoptions=' + printOptions + '&javascript=' + javascriptFunction; '&target=' + target;

					OverlayService.open({
						event: event,
						componentName: 'websys.PrintOptions.Edit',
						params: optionsParams,
						parentComponentName: $scope.componentName,
					});
					break;
				default:
					// This should not happen
					$log.warn('Printing failed: Unexpected call to websys_Print, no printing scenario managed');
			}

			return false;
		};

		var delayPrintUsingFrame = function(frame, message, url, timeOut) {
			if ((frame) && (frame.contentWindow)) {
				// Timeout to queue correctly for multiple simultaneous calls
				$timeout(function printQueue() {
					websysDisplayNotification(message);
					frame.contentWindow.location.replace(url);
				}, timeOut);
			}
		};
		var websysPrintLocal = function(printMode, linkUrl, params, target, javascriptFunction, event, localUrl) {
			if (printMode < tcConstants.PRINT_MODE.LOCAL) return false;

			if ((angular.isDefined(javascriptFunction)) && (javascriptFunction !== '')) {
				websysPrint(printMode, linkUrl, params, target, javascriptFunction, event, '');
			} else {
				websysPrint(printMode, linkUrl, params, target, '', event, {
					'printMode': printMode,
					'localUrl': localUrl,
					'params': params
				});
			}

			return false;
		};

		var websysAddListener = function(event, eventOld, handler, obj) {
			if ((angular.isUndefined(obj)) && ((event == 'load') || (event == 'beforeunload') || (event == 'unload'))) {
				obj = $scope.data;
				handler.ran = false;
			}
			if ((angular.isDefined(obj)) && (obj !== null)) {
				if (angular.isUndefined(obj.eventListeners)) {
					obj.eventListeners = {};
				}
				if (angular.isUndefined(obj.eventListeners[event])) {
					obj.eventListeners[event] = [];
				}
				obj.eventListeners[event].push(handler);

				// Backwards compatibility with TrakCare UI
				// If a click handler is assigned via addListener:
				// override the one directly assigned via 'onclick', but continue navigation for links
				if (event === 'click') {
					obj.onclick = function overrideClick() {
						return true;
					};
				}
			}
		};

		var websysRemoveListener = function(event, eventOld, handlerToRemove, obj) {
			if ((angular.isUndefined(obj)) && ((event == 'load') || (event == 'beforeunload') || (event == 'unload'))) {
				obj = $scope.data;
			}
			var handlerFound = false;
			if ((obj) && (obj !== null) && (angular.isDefined(obj.eventListeners))
			&& (angular.isDefined(obj.eventListeners[event]))) {
				angular.forEach(obj.eventListeners[event], function(handler, index, eventListeners) {
					if (angular.isUndefined(handlerToRemove) || (handlerToRemove === null)
						|| (handler === handlerToRemove)) {
						delete eventListeners[index];
						handlerFound = true;
					}
				});
			}
			if (!handlerFound) $log.warn('Handler to remove not found: ' + (handlerToRemove ? handlerToRemove.name : ''));
		};

		// Removes a class name from the item.displayStyle property
		var removeItemStyle = function(obj, className) {
			if ((angular.isDefined(obj.displayStyle)) && (obj.displayStyle !== '')) {
				var styleArray = obj.displayStyle.split(' ');
				var styleArrayFiltered = styleArray.filter(function(item) {
					return ((item !== className) && (item !== ''));
				});
				obj.displayStyle = styleArrayFiltered.join(' ');
			}
		};

		// Normalizes option parameters for websys_setClass and websys_isClass
		var websysClassOptions = function(functionName, optionsOrItem, className, classNamesToRemove) {
			optionsOrItem = (optionsOrItem || {});
			var item;
			var isTagElement = false;
			if (angular.isDefined(optionsOrItem.id)) {
				item = optionsOrItem;
			} else if (angular.isObject(optionsOrItem.item)
						|| (angular.isString(optionsOrItem.tag) && (optionsOrItem.tag !== ''))
						|| (angular.isArray(optionsOrItem.tag) && (optionsOrItem.tag.length)
						&& (angular.isString(optionsOrItem.tag[0])) && (optionsOrItem.tag[0] !== ''))) {
				if (angular.isObject(optionsOrItem.item)) {
					item = optionsOrItem.item;
				} else {
					item = optionsOrItem.tag;
					isTagElement = true;
				}
				className = optionsOrItem.className;
				classNamesToRemove = optionsOrItem.classNamesToRemove;
			} else {
				$log.warn($scope.componentName, ': ', functionName, ': item, options.item or options.tag required');
				return false;
			}
			if (!angular.isString(className)) {
				if (angular.isObject(classNamesToRemove)) {
					// For websys_setClass: allow only removing classes without passing className argument
					className = '';
				} else {
					$log.warn($scope.componentName, ': ', functionName, ': className or options.className required');
					return false;
				}
			}
			if ((functionName === 'websys_setClass') && !angular.isArray(item)) {
				item = [item];
			}
			return {
				item: item,
				className: className,
				classNamesToRemove: classNamesToRemove,
				isTagElement: isTagElement
			};
		};

		/**
		* @ngdoc method
		* @name websys_setClass
		* @methodOf tcApp.WebsysLegacyScope
		* @description
		* Changes an item or tagged elements state or display style class. For items, optionally removes other states.
		* @param {object} optionsOrItem - Options object, or item for which to set the state/class name.
		* 						With options object: separate className/classNamesToRemove parameters are ignored.
		* @param {object=} optionsOrItem.item - With options object: the item for which to set the state/class name.
		* 										optionsOrItem.item and optionsOrItem.tag parameters cannot be combined.
		* @param {string} [optionsOrItem.className] - With options object: the class name to apply. See details below
		* @param {string | string[] | undefined} optionsOrItem.tag - With options object: The tag element for which to set the class name
		* @param {object} [optionsOrItem.classNamesToRemove] - With options object: Specific states or class names to remove
		* @param {string=} className - Class name to apply. When set to blank string, removes all states by default.
		* For component items, supports the following class name strings:
		* - disabledField/clsButtonDisabled: Sets the disabled state
		* - clsRequired: Sets the required state
		* - clsReadOnly/clsReadOnlyCheckbox: Sets the read only state
		* - clsBoldLink: Sets the bold state of a link
		* - clsInvalid: Sets the invalid state
		* For tag elements, supports the values from ResponsiveItemStyle standard type ('Bold', 'Italic', etc.)
		* @param {object=} classNamesToRemove - Specific states or class names to remove,
		*				  					 as per those supported by className parameter
		*/
		var websysSetClass = function(optionsOrItem, className, classNamesToRemove) {
			var options = websysClassOptions('websys_setClass', optionsOrItem, className, classNamesToRemove);
			if (!options) return;
			className = options.className;
			classNamesToRemove = options.classNamesToRemove;

			angular.forEach(options.item, function setClassForItem(item) {
				if (options.isTagElement) {
					$scope.data.tagElements = ($scope.data.tagElements || {});
					$scope.data.tagElements[item] = ($scope.data.tagElements[item] || {});
				}
				if (angular.isObject(classNamesToRemove)) {
					angular.forEach(classNamesToRemove, function(classNameToRemove) {
						if ((classNameToRemove === 'disabledField') || (classNameToRemove === 'clsButtonDisabled')) {
							if (options.isTagElement) {
								$scope.data.tagElements[item].disabled = false;
							} else {
								// Don't allow enabling links with a Display Only conditional expression
								if ((item.displayType === 'A') && (item.displayOnly)) return;
								item.disabled = false;
							}
						} else if (classNameToRemove === 'clsRequired') {
							item.required = false;
						} else if ((classNameToRemove === 'clsReadOnly') || (classNameToRemove === 'clsReadOnlyCheckbox')) {
							item.readOnly = false;
						} else if (classNameToRemove === 'clsBoldLink') {
							if (options.isTagElement) {
								$scope.data.tagElements[item].bold = false;
							} else {
								removeItemStyle(item, 'linkBold');
							}
						} else if (classNameToRemove === 'clsInvalid') {
							LegacyScopeUtilsService.setInputValidity($scope, item, 'invalid', true);
							LegacyScopeUtilsService.setInputValidity($scope, item, 'serverWarning', true);
							LegacyScopeUtilsService.setInputValidity($scope, item, 'serverInformation', true);
							LegacyScopeUtilsService.setInputValidity($scope, item, 'serverError', true);
						} else if (angular.isDefined(tcConstants.COMPONENT_ITEM_STYLES[classNameToRemove.toUpperCase()])) {
							if (options.isTagElement) {
								removeItemStyle($scope.data.tagElements[item],
												tcConstants.COMPONENT_ITEM_STYLES[classNameToRemove.toUpperCase()]);
							} else {
								removeItemStyle(item, tcConstants.COMPONENT_ITEM_STYLES[classNameToRemove.toUpperCase()]);
							}
						}
					});
				}

				if ((className === 'disabledField') || (className === 'clsButtonDisabled')) {
					if (options.isTagElement) {
						$scope.data.tagElements[item].disabled = true;
					} else {
						item.disabled = true;
					}
				} else if (className === 'clsRequired') {
					item.required = true;
				} else if ((className === 'clsReadOnly') || (className === 'clsReadOnlyCheckbox')) {
					item.readOnly = true;
				} else if (className === 'clsInvalid') {
					LegacyScopeUtilsService.setInputValidity($scope, item, 'invalid', false);
					LegacyScopeUtilsService.getFormItem($scope, item.id)?.$setDirty();
				} else if (className === 'clsBoldLink') {
					if (options.isTagElement) {
						$scope.data.tagElements[item].bold = true;
					} else {
						if (angular.isUndefined(item.displayStyle)) item.displayStyle = '';
						// Only add the style if it isn't already there
						if (/ +linkBold{1} +/g.test(' ' + item.displayStyle + ' ') == false) {
							item.displayStyle += (((item.displayStyle !== '') ? ' ' : '') + 'linkBold');
						}
					}
				} else if (options.isTagElement && angular.isDefined(tcConstants.COMPONENT_ITEM_STYLES[className.toUpperCase()])) {
					$scope.data.tagElements[item].displayStyle = tcConstants.COMPONENT_ITEM_STYLES[className.toUpperCase()];
				} else if ((className === '') && (!angular.isObject(classNamesToRemove))) {
					// Remove all of the field states by default
					if (options.isTagElement) {
						angular.forEach(tcConstants.COMPONENT_ITEM_STYLES, function(styleToRemove) {
							removeItemStyle($scope.data.tagElements[item], styleToRemove);
						});
						$scope.data.tagElements[item].bold = false;
						$scope.data.tagElements[item].disabled = false;
					} else {
						if ((item.displayType !== 'A') || (!item.displayOnly)) {
							item.disabled = false;
						}
						item.required = false;
						item.readOnly = false;
						removeItemStyle(item, 'linkBold');
						LegacyScopeUtilsService.setInputValidity($scope, item, 'invalid', true);
						LegacyScopeUtilsService.setInputValidity($scope, item, 'serverWarning', true);
						LegacyScopeUtilsService.setInputValidity($scope, item, 'serverInformation', true);
						LegacyScopeUtilsService.setInputValidity($scope, item, 'serverError', true);
					}
				}
			});
		};

		/**
		* @ngdoc method
		* @name websys_isClass
		* @methodOf tcApp.WebsysLegacyScope
		* @description
		* Returns true if an item or tagged element matches the state/class name specified
		* @param {object} optionsOrItem - Options object, or item for which to check the state/class name against.
		* 						When an options object is used, separate className parameter is ignored.
		* @param {object=} optionsOrItem.item - When using an options object: the item to check the state/class name
		* @param {string} optionsOrItem.className - When using an options object: the state/class name to check
		* @param {string=} optionsOrItem.tag - When using an options object: the tag element to check the class name against
		* @param {string=} className - Class name to check. Required unless options object className property is set.
		* For component items, supports the following class name strings:
		* - disabledField/clsButtonDisabled: Checks the disabled state
		* - clsRequired: Checks the required state
		* - clsReadOnly/clsReadOnlyCheckbox: Checks the read only state
		* - clsBoldLink: Checks the bold state of a link
		* - clsInvalid: Checks the invalid state
		* For tag elements, supports the values from ResponsiveItemStyle standard type ('Bold', 'Italic', etc.)
		*/
		var websysIsClass = function(optionsOrItem, className) {
			var options = websysClassOptions('websys_isClass', optionsOrItem, className);
			if (!options) return false;
			var item = options.item;
			className = options.className;

			if (options.isTagElement) {
				if (angular.isUndefined($scope.data.tagElements)) return false;
				if (angular.isUndefined($scope.data.tagElements[item])) return false;
				if (className === 'clsBoldLink') {
					return !!$scope.data.tagElements[item].bold;
				} else if ((className === 'disabledField') || (className === 'clsButtonDisabled')) {
					return !!$scope.data.tagElements[item].disabled;
				} else {
					var componentItemStyle = tcConstants.COMPONENT_ITEM_STYLES[className.toUpperCase()];
					if (angular.isUndefined(componentItemStyle)) return false;
					return ($scope.data.tagElements[item].displayStyle === componentItemStyle);
				}
			} else {
				if ((className === 'clsRequired') && (item.required)) return true;
				if (((className === 'clsReadOnly') || (className === 'clsReadOnlyCheckbox')) && (item.readOnly)) return true;
				if (((className === 'disabledField') || (className === 'clsButtonDisabled')) && (item.disabled)) return true;
				if ((className === 'clsBoldLink') && angular.isDefined(item.displayStyle)
					&& ((' ' + item.displayStyle + ' ').indexOf(' linkBold ') !== -1)) return true;
				if (className === 'clsInvalid') {
					var formItem = LegacyScopeUtilsService.getFormItem($scope, item.id);
					if (angular.isDefined(formItem)) {
						// Blank unchanged field that is required should not be considered invalid
						if (item.required && (item.value === '') && formItem.$pristine) return false;
						formItem.$validate();
						if (formItem.$invalid) return true;
					}
				}
			}

			return false;
		};

		/**
		* @ngdoc method
		* @name websys_fireEvent
		* @methodOf tcApp.WebsysLegacyScope
		* @description
		* Manually fires the specified event on the object provided.
		* Primarily used to fire change or click handler after a value has been programmatically set
		* @param {object} obj Object for which to fire the event
		* @param {string} eventName Name of the the event to fire, i.e. 'change'/'click'/'keydown'
		* @param {object=} documentObject [TCUI ONLY] Document that the event should be attached to. Ignored in MEUI
		* @param {boolean=} calcDate For date fields, indicate that the field was set using Calculate Date control
		* @param {object=} eventDetail [TCUI ONLY] The detail property for CustomEvents. Ignored in MEUI
		*/
		var websysFireEvent = function(obj, eventName, documentObject, calcDate, eventDetail) {
			if (!angular.isObject(obj) || !angular.isString(eventName)) return;
			if (eventName === 'change') {
				// For lookup brokers, 'tkencmeth' is generated
				// Trigger the lookup broker to convert the value, and then lookup & onchange functions
				// $timeout with 100 second delay so $setDirty not cleared by $setPrestine in compScriptService
				// when calling this from bodyload or TRELOADJS
				var onLoadRan = $scope.viewModel.onLoadRan;
				$timeout(function websysFireEventChange() {
					if (angular.isDefined(obj.tkencmeth) && (!obj.hiddenItem)) {
						$scope.$broadcast('tcLookupContainer:changeValue', obj);
					} else {
						$scope.itemChangeHandler(obj, obj._listRowIndex, calcDate);
					}
					// When changing a field after the load handler is done,
					// set the form item to dirty so messages will display if required
					if (onLoadRan && angular.isDefined($scope.componentForm[$scope.componentHtmlName])
							&& angular.isDefined($scope.componentForm[$scope.componentHtmlName][obj.id])) {
						$scope.componentForm[$scope.componentHtmlName][obj.id].$setDirty();
					}
				}, 100);
			} else if (eventName === 'click') {
				if (angular.isFunction(obj.onChangeHandler)) {
					obj.onChangeHandler(obj);
				}
				// Allow for special logic where link/button should be triggered from a separate UI element
				// (e.g. Logon page when selecting a logon location)
				if ((obj.displayType === 'A') || (obj.displayType === 'B')) {
					// For buttons, don't continue where the form is initialized but invalid
					if ((obj.displayType === 'B')
						&& (angular.isDefined($scope.componentForm[$scope.componentHtmlName]))
						&& ($scope.componentForm[$scope.componentHtmlName].$invalid)) return;

					// Can be fired from another click handler, so reset this flag
					$scope.processingEvent = false;

					let itemName = obj.id;
					let listRowIndex,dynamicColumnIndex;
					// check to see if event item is table item
					const itemNameMatch = itemName.match(/(?<itemName>.+)z\d+$/);
					if (itemNameMatch) {
						itemName = (itemNameMatch.groups?.itemName ?? obj.id);
						listRowIndex = obj._listRowIndex;
						dynamicColumnIndex = obj._dynamicColumnIndex;
					}
					$scope.processEvent({
						eventItemName: itemName,
						listRowIndex,
						dynamicColumnIndex,
					}, null);
				}
			} else if (eventName === 'keydown') {
				$timeout(function() {
					$scope.itemKeyDownHandler(obj);
				}, 100);
			}
		};

		var websysDisplayNotification = function(message, title) {
			ToastService.show({
				message: message,
				title: title
			});
		};

		var websysDisplayWarnNotification = function(message, title) {
			ToastService.show({
				message: message,
				title: title,
				type: 'error'
			});
		};

		var websysSetFocus = function(id, item) {
			if (angular.isUndefined(item)) item = websysGetItem(id);
			if ((angular.isDefined(item)) && (item !== null)) {

				LegacyScopeUtilsService.expandSectionForFocusedItem($scope, item);
				var itemId = LegacyScopeUtilsService.getHtmlIdForItem($scope, item);

				$timeout(function setFocusOnElement() {
					var element;
					if (item.displayType === 'HRT') {
						if (angular.isDefined($window.CKEDITOR)) {
							element = $window.CKEDITOR.instances[item.id];
							if ((angular.isDefined(element)) && (element.instanceReady) && (angular.isFunction(element.focus))) {
								element.focus();
							}
						}
					} else {
						element = $window.document.getElementById(itemId);
						if (element && ($window.document.activeElement !== element)) {
							if ((element.tagName === 'INPUT') || (element.tagName === 'TEXTAREA')) {
								element.select();
							} else if (item.displayType === 'TAB') {
								var tabElement = element.querySelector('.md-tab.md-active');
								if (tabElement) tabElement.focus();
							} else if (angular.isFunction(element.focus)) {
								element.focus();
							}
						}
					}
					// Scroll to the focused element within the component body. Add an offset, in case of horizontal scrollbar
					if ((element) && (!element.classList.contains('buttonBarButton'))) {
						GlobalService.scrollElementIntoView(element, element, null, 50);
					}
				}, 100);
			} else {
				$log.warn($scope.componentName, ': websys_setfocus could not find item ' + id);
			}
		};

		var websysCanFocus = function(item) {
			if (angular.isUndefined(item)) return false;

			return ((!item.hiddenItem) && (!item.disabled) && (!item.hidden) && (!item.displayOnly));
		};

		var websysGetBgColor = function() {
			$log.warn($scope.componentName, ': Call to websys_getBgColor');
			return '';
		};

		var websysIsDocumentReady = function() {
			$log.warn($scope.componentName, ': Call to websys_isDocumentReady');
			return true;
		};

		var websysOverlay = function(options) {
			var item;
			if (angular.isObject(options)) {
				if (angular.isDefined(options.popoverFor)) item = websysGetItem(options.popoverFor);
				if ((angular.isDefined(item)) && (item !== null)) {
					options.popoverFor = LegacyScopeUtilsService.getHtmlIdForItem($scope, item);
				}
				OverlayService.open(options);
			} else {
				$log.warn($scope.componentName, ': websys_Overlay expecting options object');
			}
			return false;
		};

		var websysIsInAccordion = function() {
			return false;
		};

		var websysFindInfoPaneOpener = function() {
			$log.warn($scope.componentName, ': Call to websys_FindInfoPaneOpener');
			return null;
		};

		var websysInInfoPane = function() {
			return false;
		};

		var PassReportParametersForPreview = function(path, newwin, p0, p1, p2, p3, p4, p5, p6, p7, p8) {
			if ((angular.isUndefined(path)) || (path === '')) {
				$log.warn($scope.componentName, ': No print preview URL specified');
				return;
			}
			var temp = '';
			var newPath = '';
			var arrParam,maskedUrl;
			if ((path.match('zenpreview.csp') !== null) || (path.match('jreportpreview.csp') !== null)) {
				arrParam = path.split('&P1');
				newPath = arrParam[0];
				for (var i = 0; i < 9; i++) {
					var x = i + 1;
					newPath += '&P' + x + '=';
					//if parameter defined, pass along to report. these parameters are usually the specific values of the selected row
					if (arguments[i + 2]) {
						newPath += arguments[i + 2];
					} else {
						// take value from original link
						temp = mPiece(path, 'P' + x + '=', 1);
						newPath += mPiece(temp, '&', 0);
					}
				}
			} else {
				arrParam = path.split('&prompt');
				newPath = arrParam[0];

				for (i = 0; i < 9; i++) {
					newPath += '&prompt' + i + '=';
					//if parameter defined, pass along to report. these parameters are usually the specific values of the selected row
					if (arguments[i + 2]) {
						newPath += arguments[i + 2];
					} else {
						// take value from original link
						temp = mPiece(path, 'prompt' + i + '=', 1);
						newPath += mPiece(temp, '&', 0);
					}
				}
				if ((path.indexOf('&reportmanagerdsn=') !== -1) && (path.indexOf('&configmanagerdsn=') !== -1)) {
					var str = path.split('&reportmanagerdsn=');
					var dsnstr = str[1].split('&configmanagerdsn=');
					newPath = newPath + '&reportmanagerdsn=' + dsnstr[0] + '&configmanagerdsn=' + dsnstr[1];
				}
			}

			if (newwin == 'TRAK_hidden') {
				// use standard print function to print in hidden frame
				websysPrintLocal(tcConstants.PRINT_MODE.LOCAL, '', '', '', '', '', newPath);
			} else {
				maskedUrl = GlobalService.maskUrl(newPath);
				websysOpen(maskedUrl);
			}
			return false;
		};

		var websysEscape = function(str) {
			//used in place of javascript escape which causes problems with Greek which gets treated as unicode
			//must convert the % first.
			if (str.indexOf('%') > -1) {
				str = str.split('%').join('%25');
			}
			while (str.indexOf('?') > -1) {
				str = str.replace('?', '%3F');
			}
			while (str.indexOf('=') > -1) {
				str = str.replace('=', '%3D');
			}
			while (str.indexOf(' ') > -1) {
				str = str.replace(' ', '%20');
			}
			while (str.indexOf('"') > -1) {
				str = str.replace('"', '%22');
			}
			while (str.indexOf('\'') > -1) {
				str = str.replace('\'', '%27');
			}
			while (str.indexOf('&') > -1) {
				str = str.replace('&', '%26');
			}
			while (str.indexOf('#') > -1) {
				str = str.replace('#', '%23');
			}
			while (str.indexOf(String.fromCharCode(13, 10)) > -1) {
				str = str.replace(String.fromCharCode(13, 10), '%0D%0A');
			}
			if (str.indexOf('+') > -1) {
				str = str.split('+').join('%2B');
			}
			while (str.indexOf(String.fromCharCode(1)) > -1) {
				str = str.replace(String.fromCharCode(1), '%01');
			}
			while (str.indexOf(String.fromCharCode(2)) > -1) {
				str = str.replace(String.fromCharCode(2), '%02');
			}
			while (str.indexOf(String.fromCharCode(4)) > -1) {
				str = str.replace(String.fromCharCode(4), '%04');
			}
			return str;
		};

		// returns editor object
		var websysGetEditor = function(obj, id, componentName) {
			var editor = '';
			if (angular.isDefined($window.CKEDITOR) && $window.CKEDITOR.status === 'loaded') {
				if (angular.isString(obj) && (obj !== '')) {
					id = obj;
					obj = '';
				}
				if (!obj && (id !== '')) {
					obj = websysGetItem(id, componentName);
				}
				if (obj && angular.isDefined($window.CKEDITOR)) {
					editor = $window.CKEDITOR.instances[obj.id];
				}
			}
			return editor;
		};

		/**
		 * Returns a component item object, optionally from another component
		 * @param {string} id - Id of the item to retrieve
		 * @param {string} [componentName] - Optional component name, when retrieving the item on another component
		 * @returns Component item object, or undefined if one wasn't found
		 */
		var websysGetItem = function(id, componentName) {
			if (!id) return undefined;

			if ((!componentName) || (componentName === '') || (componentName === $scope.componentName)) {
				return LegacyScopeSearchService.getItemFromDataObject($scope, id, true);
			} else {
				var item;
				// Broadcast the event to be picked up by the specified component and set the item returned by that component
				GlobalService.getComponentItem(id, componentName, function(returnedItem) {
					// Where there are multiple components with the same name, the returnedItem for the last component will be set
					item = returnedItem;
				});
				return item;
			}
		};

		// Get value of any item object, optionally from another component
		var websysGetValue = function(id, componentName) {
			var item = websysGetItem(id, componentName);
			if (item) {
				if ((item.displayType === 'HRT') || ((item.displayType === 'HTB') && !item.displayOnly)) {
					if ((angular.isDefined($window.CKEDITOR)) && (angular.isDefined($window.CKEDITOR.instances[id]))) {
						return $window.CKEDITOR.instances[id].getData().replace(/<\/?p>|\r?\n|\r/gi, '');
					} else {
						$log.warn('websys_getValue: Could not find instance of CKEDITOR: ', id);
					}
				} else {
					return item.value;
				}
			}
			return '';
		};

		// Set value of any item object, optionally on another component
		var websysSetValue = function(id, value, componentName) {
			var item = websysGetItem(id, componentName);
			if (!item) return;

			if ((item.displayType === 'HRT') || ((item.displayType === 'HTB') && !item.displayOnly)) {
				LegacyScopeUtilsService.runOnEditorReady($scope, id, function setValueOnEditorReady(editor) {
					// Note issue with multiple subsequent calls:
					// https://github.com/ckeditor/ckeditor4/issues/3669
					editor.setData(value);
				}, componentName);
			} else if (item.displayType === 'L') {
				if (value === '') {
					item.options = [];
					item.input = '';
				} else if (angular.isArray(value)) {
					item.options = value;
					if (!value.length) item.input = '';
				}
			} else {
				item.value = value;
			}
			// Also add or remove any reload function to the WorkflowService map
			if (item.id === 'TRELOADJS') {
				var dialogLevel = WorkflowService.getParentDialogLevel();
				if (angular.isUndefined(value) || (value === '')) {
					WorkflowService.removeReloadFunctionForDialogLevel(dialogLevel);
				} else {
					if (!WorkflowService.addReloadFunctionToMap(value, dialogLevel)) {
						$log.warn($scope.componentHtmlName,
							': websys_setValue: Unable to set callback/reload function via TRELOADJS item');
					}
				}
			}
		};

		var websysGetText = function(obj) {
			if (!obj) return '';
			return websysGetValue(obj.id);
		};

		var websysSetText = function(obj, text) {
			if (!obj) return;
			return websysSetValue(obj.id, text);
		};

		var websysCallFunction = function(componentName, jsFunction, args, callbackFunction) {
			var componentFound = false;
			var callFunction = function callFunction(componentHtmlName) {
				if ((angular.isDefined(args)) && (!angular.isArray(args))) args = [args];
				GlobalService.callFunction.apply(this, [componentHtmlName, jsFunction, args, callbackFunction]);
				componentFound = true;
			};

			// Find the first matching component up the tree
			$scope.$emit('Component:getComponentHtmlName', componentName, callFunction);
			if (!componentFound) {
				// Otherwise do a full search from the root scope (e.g. for siblings)
				$rootScope.$broadcast('Component:getComponentHtmlName', componentName, callFunction);
				// If no componentHtmlName match, callFunction with componentName for custom modules (e.g. ACN, Floorplan)
				if (!componentFound) callFunction(componentName);
			}
		};

		var websysReloadSession = function() {
			// Reload all logon data and templates
			GlobalService.loginHandler().then(function() {
				// Remove all component layout templates, as we cannot determine which are associated via preferences to the
				// security group, user, etc.
				GlobalService.removeCachedLayoutTemplate();
				if (angular.isDefined(GlobalService.logonData.startWorklist)) {
					$window.location.href = (GlobalService.getAppClassName() + '#/Worklist');
				} else {
					$window.location.href = (GlobalService.getAppClassName() + '#/Workflow');
				}
			}, function(error) {
				$log.error($scope.componentName, ': websys_reloadSession error: ', error);
			});
		};

		var websysRemoveCachedLayoutTemplate = function(componentName) {
			return GlobalService.removeCachedLayoutTemplate(componentName);
		};

		var websysRemoveAllComponentCache = function() {
			ComponentCache.removeAll();
		};

		var websysPrintGetRequestParams = function(selectedIndexes, staticParams, listItemMap) {
			/*
			From a set of selected indexes, build a set of request parameters
			First add static parameters that are passed in
			Then loop through the list and map the items onto the request parameters, given the listItemMap.
			listItemMap is an object in this format e.g.:
			{
				'PatientID': '=PatientID',
				'RequestAndListKeyDiffer': '=EpisodeID',
				'SomeStaticValue': 'my value'
			}

			'=' references the value from the table item of the row
			Otherwise the item is a literal value
			*/

			if ((!staticParams) && (!listItemMap)) return null;
			var requestParams;

			// First include any simple value parameters
			if (angular.isObject(staticParams)) {
				requestParams = staticParams;
			} else {
				requestParams = {};
			}

			// Then add each item for the selected rows as an array of values
			if ((selectedIndexes) && (listItemMap) && (angular.isDefined($scope.data.listRows))) {
				var selectedRow,listItem,listItemValue;
				for (var i = 0; i < selectedIndexes.length; i++) {
					// Note: selectedIndexes is 1-indexed
					selectedRow = $scope.data.listRows[selectedIndexes[i] - 1];
					if (angular.isUndefined(selectedRow)) {
						$log.warn('websys_printGetRequestParams: Incorrect index value');
						continue;
					}

					angular.forEach(listItemMap, function(mapItem, mapKey) {
						listItemValue = undefined;
						if (mapItem.substring(0, 1) === '=') {
							listItem = selectedRow[mapItem.substring(1)];
							if (angular.isDefined(listItem)) {
								// Value from list item
								listItemValue = listItem.value;
							}
						} else {
							// Static value
							listItemValue = mapItem;
						}
						if (angular.isDefined(listItemValue)) {
							if (angular.isDefined(requestParams[mapKey])) {
								requestParams[mapKey].push(listItemValue);
							} else {
								requestParams[mapKey] = [listItemValue];
							}
						}
					});
				}
			}

			return requestParams;
		};

		/**
		* @ngdoc method
		* @name websys_replaceLocationParams
		* @methodOf tcApp.WebsysLegacyScope
		* @description
		* 	Replaces request parameters for the current URL, OR prevents breadcrumb creation for page navigations.
		* 	Where the current URL is masked and newParams are unmasked: existing request parameter values will be replaced,
		* 	and a new masked URL will be generated.
		* 	When newParams is not passed, this function will prevent the next page navigation from creating a breadcrumb.
		* @param {string | object | undefined} newParams [optional] Request parameters to replace. Can be a URL search string or an object
		* 	with the request parameter key/values.
		* 	An already-masked URL may also be passed as newParams, in which case the URL will be replaced rather than indivdual request parameters.
		*/
		var websysReplaceLocationParams = function(newParams) {
			if ((angular.isUndefined(newParams)) || (newParams === '')) {
				$scope.location.replace();
				// When navigating without a breadcrumb, do not use component cache
				if (angular.isDefined($scope.data)) $scope.data.doNotCache = true;
				return;
			}

			if (angular.isUndefined(newParams)) newParams = {};
			if (angular.isString(newParams)) newParams = GlobalService.urlToObject(newParams);
			if (Object.keys(newParams).length === 0) {
				$log.warn($scope.componentName, ': websys_replaceLocationParams: newParams argument requires at least'
					+ ' one request parameter');
				return;
			}
			if (angular.isUndefined(newParams['TUID'])) {
				// Any new or replaced parameters should be encoded and the URL re-masked
				// If newParams is already TUID-encoded, assume this has been done already (i.e. on the server side)
				var newParamsSerialized = ('&' + $httpParamSerializer(newParams));
				var existingParams = $location.search();
				// Move the TUIDs to the front of the alphabetically sorted serialized string..
				var existingTUIDsSerialized = $httpParamSerializer({'TUID': existingParams['TUID']});
				delete existingParams['TUID'];
				var existingParamsSerialized = ('?' + existingTUIDsSerialized + '&' +
					$httpParamSerializer(existingParams));
				var newMaskedUrl = GlobalService.maskUrl(existingParamsSerialized, newParamsSerialized);
				var newMaskedUrlParams = newMaskedUrl.split('?')[1];
				if (!angular.isString(newMaskedUrlParams)) return;

				newParams = GlobalService.urlToObject(newMaskedUrlParams);
			} else if (angular.isDefined($routeParams['Dialog'])
				&& angular.isUndefined(newParams['Dialog'])) {
				// Preserve the breadcrumb level parameter if one exists
				newParams['Dialog'] = $routeParams['Dialog'];
			}
			ComponentCache.remove(angular.toJson($routeParams));
			ComponentCache.remove(angular.toJson(newParams));
			$scope.location.search(newParams).replace();
		};

		// Used when reloading a parent component on update. Sets component name that is used in processEvent()
		var websysSetReloadComponent = function(componentNames, paramFunctions) {
			if (angular.isString(componentNames)) componentNames = [componentNames];
			if (angular.isFunction(paramFunctions)) paramFunctions = [paramFunctions];

			var componentFound;
			var paramFunction;
			var setReloadComponent = function setReloadComponent(componentHtmlName) {
				if (angular.isDefined(componentHtmlName)) {
					if ($scope.viewModel.processEventOptions.reloadComponent) {
						$scope.viewModel.processEventOptions.reloadComponent.push(componentHtmlName);
					} else {
						$scope.viewModel.processEventOptions.reloadComponent = [componentHtmlName];
					}
					if (angular.isDefined(paramFunction)) {
						if (angular.isDefined($scope.viewModel.processEventOptions.reloadComponentParamFunction)) {
							$scope.viewModel.processEventOptions.reloadComponentParamFunction.push(paramFunction);
						} else {
							$scope.viewModel.processEventOptions.reloadComponentParamFunction = [paramFunction];
						}
					}
					componentFound = true;
				}
			};

			angular.forEach(componentNames, function(componentName, componentIndex) {
				componentFound = false;
				if (angular.isDefined(paramFunctions)) paramFunction = paramFunctions[componentIndex];
				// Find the first matching component up the tree
				$scope.$emit('Component:getComponentHtmlName', componentName, setReloadComponent);
				if (!componentFound) {
					// Otherwise do a full search from the root scope (e.g. for siblings)
					$rootScope.$broadcast('Component:getComponentHtmlName', componentName, setReloadComponent);
					if (!componentFound) $log.warn('No component found: ' + componentName);
				}
			});
		};

		// Used when reloading an entire route on a component event
		var websysSetReloadRoute = function() {
			$scope.viewModel.processEventOptions.reloadRoute = true;
		};

		/**
		 * @ngdoc method
		 * @name websys_setReturnValue
		 * @methodOf tcApp.WebsysLegacyScope
		 * @description
		 * Used to pass a return value parameter to a TRELOADJS function
		 * @param {any} returnValueOrOptions The return value parameter to pass to the TRELOADJS function, or an options object.
		 * 	When an object is passed with a 'returnValue' property, acts as an options object with the properties:
		 * @param {any} 	returnValueOrOptions.returnValue The return value parameter to pass to the TRELOADJS function (when an options object is used)
		 * @param {string=} returnValueOrOptions.reloadFunctionName Allows targetting a return value parameter only for a specific TRELOADJS function
		 * @param {string=} unusedComponentName (Unused in MEUI, only used in TCUI)
		 * @returns {boolean} Returns true if a reload function was found for which to set the return value
		 */
		var websysSetReturnValue = function(returnValueOrOptions, unusedComponentName) {
			var returnValue = returnValueOrOptions;
			var reloadFunctionName = '';
			if (angular.isObject(returnValueOrOptions) && angular.isDefined(returnValueOrOptions.returnValue)) {
				returnValue = returnValueOrOptions.returnValue;
				reloadFunctionName = returnValueOrOptions.reloadFunctionName;
			}
			var dialogLevel = WorkflowService.getParentDialogLevel();
			var reloadFunctionFound = WorkflowService.setReloadFunctionReturnValue(returnValue, dialogLevel, reloadFunctionName);
			return reloadFunctionFound;
		};

		// Used to set an external authentication URL for a Password field
		var websysSetExternalAuthUrl = function(key, url) {
			key = (key ? key : 'PIN');
			var item = LegacyScopeSearchService.getItemFromDataObject($scope, key, false);
			if (item) item.externalAuthUrl = url;
		};

		/**
		 * @description
		 * Dynamically sets the component caption, and optionally the breadcrumb and page title.
		 * @param {string} caption The caption to set
		 * @param {boolean} updateBreadcrumb Whether to update the breadcrumb and page title with the caption
		 */
		var websysSetComponentCaption = function(caption, updateBreadcrumb) {
			if (!angular.isString(caption)) return;

			$scope.data.componentHeaderCaption = caption;
			if (updateBreadcrumb) BreadcrumbService.updateCurrentCaption(caption);
		};

		// Used to dynamically set Item or list Row Status Indicator class/colour
		var websysSetStatusClass = function(obj, className, colour, listRowIndex) {
			if ((angular.isDefined(listRowIndex)) && (angular.isDefined($scope.data.listRows))) {
				var listRowObj = $scope.data.listRows[listRowIndex];
				if (angular.isObject(listRowObj)) {
					if ((angular.isDefined(className)) && (className !== '')) {
						listRowObj._listRowClass = className;
						listRowObj._listRowColour = '';
					} else if ((angular.isDefined(colour)) && (colour !== '')) {
						listRowObj._listRowClass = '';
						listRowObj._listRowColour = colour;
					} else {
						listRowObj._listRowClass = '';
						listRowObj._listRowColour = '';
					}
				}
			} else if (angular.isObject(obj)) {
				if ((angular.isDefined(className)) && (className !== '')) {
					obj.statusClass = className;
					obj.statusColour = '';
				} else if ((angular.isDefined(colour)) && (colour !== '')) {
					obj.statusClass = '';
					obj.statusColour = colour;
				} else {
					obj.statusClass = '';
					obj.statusColour = '';
				}
			}
		};

		// Closes the current overlay
		var websysOverlayClose = function() {
			// Broadcast directly from rootScope to avoid issues with complex multi-component scenarios
			$rootScope.$broadcast('Overlay:Close');
		};

		/**
		* @ngdoc method
		* @name websys_loadNestedComponent
		* @methodOf tcApp.WebsysLegacyScope
		* @description
		* Loads or unloads a specific component into a nested component item, with optional parameters and callback function
		* @param {object} Options object with properties:
		* 	- itemName: Nested component item name where the nested component should be loaded
		* 	- itemComponentName [optional]: Component that contains the nested component item
		* 	- componentName: Nested component to load. Passing empty string will unload the nested component.  Ignored when reload is true.
		*   - reload [optional]: if true, will ignore componentName and reload the current component present in the item (similar to _Reload functions)
		* 	- params [optional]: Request parameters to pass to nested component. If reload is true, these are additional parameters (similar to _Reload),
		*       otherwise these are the full parameters.
		* 	- callbackFunction [optional]: Callback function to execute after the data is retrieved for the nested component
		*	  					(Note: Does not wait until nested component scripts have executed)
		* 						Will be passed a boolean value as the first argument indicating
		*						whether or not the load/unload completed successfully.
		*/
		var websysLoadNestedComponent = function(options) {
			if (!angular.isObject(options)) {
				$log.warn('websys_loadNestedComponent: Invalid options');
				return;
			}
			if ((!angular.isString(options.itemName)) || (options.itemName === '')) {
				$log.warn('websys_loadNestedComponent: options.itemName is required');
				return;
			}
			if (!angular.isString(options.params)) options.params = '';
			if (options.reload) {
				if (angular.isString(options.componentName)) {
					$log.warn('websys_loadNestedComponent: called with reload and componentName; componentName will be ignored');
				}
			} else if (!angular.isString(options.componentName)) {
				$log.warn('websys_loadNestedComponent: options.componentName is required');
				return;
			}

			// Where another component is specified, call the function on that component
			if ((angular.isDefined(options.itemComponentName)) && (options.itemComponentName !== '')) {
				var componentFound = false;
				var callLoadNestedComponent = function callFunction(componentHtmlName) {
					var args = [options.itemName, options.componentName, options.params, options.callbackFunction, options.reload];
					GlobalService.callFunction.apply(this, [componentHtmlName, 'loadNestedComponent', args]);
					componentFound = true;
				};
				// Find the first matching component up the tree
				$scope.$emit('Component:getComponentHtmlName', options.itemComponentName, callLoadNestedComponent);
				if (!componentFound) {
					// Otherwise do a full search from the root scope (e.g. for siblings)
					$rootScope.$broadcast('Component:getComponentHtmlName', options.itemComponentName, callLoadNestedComponent);
					if (!componentFound) {
						$log.warn('websys_loadNestedComponent: Component ' + options.itemComponentName + ' not found');
						return;
					}
				}
			} else {
				// Otherwise, just call the function on the current component
				return $scope.loadNestedComponent(
					options.itemName,
					options.componentName,
					options.params,
					options.callbackFunction,
					options.reload
				);
			}
		};

		var websysRefreshNotifications = function() {
			$scope.$emit('Component:getComponentHtmlName', 'websys.Notification', function(componentHtmlName) {
				if (angular.isDefined(componentHtmlName)) {
					GlobalService.reloadComponent(componentHtmlName);
				}
			});
		};

		var navigateAwayAddCheck = function(componentName, msg, window, specialForm) {
			var componentFound = false;

			var addCheck = function addCheck(componentHtmlName) {
				if (angular.isDefined(componentHtmlName)) {
					WorkflowService.addNavigateAwayCheck(componentHtmlName, msg);
					componentFound = true;
				}
			};
			// Find the first matching component up the tree
			$scope.$emit('Component:getComponentHtmlName', componentName, addCheck);
			if (!componentFound) {
				// Otherwise do a full search from the root scope (e.g. for siblings)
				$rootScope.$broadcast('Component:getComponentHtmlName', componentName, addCheck);
			}

		};

		var navigateAwayCompleteCheck = function(componentName) {
			var componentFound = false;

			var completeCheck = function completeCheck(componentHtmlName) {
				if (angular.isDefined(componentHtmlName)) {
					WorkflowService.navigateAwayCompleteCheck(componentHtmlName);
					componentFound = true;
				}
			};
			// Find the first matching component up the tree
			$scope.$emit('Component:getComponentHtmlName', componentName, completeCheck);
			if (!componentFound) {
				// Otherwise do a full search from the root scope (e.g. for siblings)
				$rootScope.$broadcast('Component:getComponentHtmlName', componentName, completeCheck);
			}
		};

		// Returns whether one of the component's classes is locked by another process
		// (i.e. the 'lock' error message displayed on load)
		var websysIsLocked = function() {
			return ($scope.data.isLocked || false);
		};

		// Executes the function or code, with a fixed time delay (in ms) between each call
		var websysSetInterval = function(func, delay/*, arg1, arg2, ... */) {
			if ((angular.isUndefined(func)) || (!angular.isNumber(delay)) || (delay < 1)) {
				$log.warn($scope.componentName, ': websys_setInterval call with invalid arguments');
				return;
			}
			var interval;
			var args = Array.prototype.slice.apply(arguments);
			if (angular.isFunction(func)) {
				if (args.length > 2) {
					// Add the extra arguments for $interval
					args.splice(2, 0, 0, true);
				} else {
					args = args.concat([0, true, undefined]);
				}
				interval = $interval.apply(this, args);
			} else {
				interval = $window.setInterval.apply(this, args);
			}

			$scope.viewModel.intervals.push(interval);

			return interval;
		};

		// Clears an interval set by websys_setInterval
		var websysClearInterval = function(interval) {
			if (angular.isUndefined(interval)) {
				$log.warn($scope.componentName, ': websys_clearInterval call with invalid arguments');
				return;
			}
			if (angular.isNumber(interval)) {
				$window.clearInterval(interval);
			} else {
				$interval.cancel(interval);
			}
			var intervalIndex = $scope.viewModel.intervals.indexOf(interval);
			if (intervalIndex !== -1) {
				$scope.viewModel.intervals.splice(intervalIndex, 1);
			}
		};

		// Sets the caption/label for a component item
		var websysSetCaption = function(itemOrId, caption) {
			let item = itemOrId;
			if (typeof itemOrId === 'string') item = websysGetItem(itemOrId);
			if ((typeof item === 'undefined') || (item.id === '')) {
				$log.warn($scope.componentName, ': websys_setCaption call with invalid item ', item);
				return;
			}
			const itemId = ((typeof item._listRowIndex === 'number') ? item.id.split('z')[0]
						: item.id);
			if (typeof item._dynamicColumnIndex === 'number') {
				$scope.data.listDynColumnSettings[itemId].captions[item._dynamicColumnIndex] = caption;
			} else {
			$scope.data.translations[itemId] = caption;
			}
		};

		var checkRequiredActiveXLoaded = function() {
			$log.warn($scope.componentName, ': Call to CheckRequiredActiveXLoaded');
			// Return true for convenience, ActiveX is not supported
			return true;
		};

		// Hides, shows or toggles tagged layout elements
		// options.tag (required) can be a single tag (string), or an array of tags
		// options.hidden (optional) will set the hidden state, otherwise current state is toggled
		var websysToggleTagged = function(options) {
			options = options || {};
			if (angular.isUndefined(options.tag) || (options.tag === '')) {
				$log.warn($scope.componentName, ': websys_toggleTagged: options.tag is required');
				return;
			}
			options = angular.copy(options);
			if (angular.isArray(options.tag)) {
				if (options.tag.length === 0) {
					$log.warn($scope.componentName, ': websys_toggleTagged: options.tag is required');
					return;
				}
			} else {
				options.tag = [options.tag];
			}
			if (angular.isUndefined($scope.data.tagElements)) {
				$scope.data.tagElements = {};
			}
			angular.forEach(options.tag, function(tag) {
				if (angular.isUndefined($scope.data.tagElements[tag])) {
					$scope.data.tagElements[tag] = {};
				}
				if ((options.hidden === true) || (options.hidden === false)) {
					$scope.data.tagElements[tag].hidden = options.hidden;
				} else {
					// If nothing or an invalid value is passed, toggle the current state
					$scope.data.tagElements[tag].hidden = !$scope.data.tagElements[tag].hidden;
				}
			});
		};

		/**
		* @ngdoc method
		* @name websys_setMessage
		* @methodOf tcApp.WebsysLegacyScope
		* @description
		* Displays a client-side component or component item message.
		* @param {object} options object with properties:
		* - messageText: Text of the message that will display.
		* - messageType: [optional] Type of the message. 'E' (Error), 'W' (Warning) and 'I' (Info) are supported.
		*							Default is 'E' (Error) if not provided.
		* - itemName: [optional] Name of the component item to display the message against.
		*						 If not provided or the item does not exist, the message will be component based.
		* - item: [optional] Object reference to the component item to display the message against.
		*						 This is used first, otherwise if not provided is checked "itemName" parameter.
		* - confirmAction: [MEUI ONLY, required for W/I message]
		*						 Name of the component item that should be actioned when
		*						 user clicks 'Confirm' on a Warning or Info message.
		* - keepExisting: [MEUI ONLY, optional] Set to true to keep existing item messages before displaying the new message.
		*							 Only applicable for Error type messages on component items.
		* @returns {boolean} Returns false if invalid options are passed. Returns true for Error messages.
		*					 1. For MEUI logic, returns false when initially displaying a Warning or Info message.
		*					    Returns true if called again from the confirmAction event for a Warning/Info message.
		*					    (i.e. User clicks Confirm and the confirmAction item runs the same click handler)
		*					 2. For TCUI logic, returns true/false if user clicks OK/Cancel on a Warning/Info message.
		* }
		*/
		var websysSetMessage = function(options) {
			options = options || {};
			if (!angular.isString(options.messageText) || (options.messageText === '')) {
				$log.warn($scope.componentName, ': websys_setMessage: Message text is required');
				return false;
			}
			if (!angular.isString(options.messageType) ||
				([tcConstants.COMPONENT_MESSAGE_TYPE.ERROR,
				tcConstants.COMPONENT_MESSAGE_TYPE.WARNING,
				tcConstants.COMPONENT_MESSAGE_TYPE.INFO].indexOf(options.messageType) === -1)) {
				options.messageType = tcConstants.COMPONENT_MESSAGE_TYPE.ERROR;
			}
			if (([tcConstants.COMPONENT_MESSAGE_TYPE.WARNING,
				tcConstants.COMPONENT_MESSAGE_TYPE.INFO].indexOf(options.messageType) !== -1)
				&& ((!angular.isString(options.confirmAction)) || (options.confirmAction === ''))) {
				$log.warn($scope.componentName,
							': websys_setMessage: options.confirmAction is required for Warning/Info type message');
				return false;
			}

			var item = LegacyScopeSearchService.getItemFromOptions($scope, options, false);
			if ((!item) && (angular.isString(options.itemName)) && (options.itemName !== '')) {
				$log.warn($scope.componentName, ': websys_setMessage: Item ', options.itemName, ' could not be found');
			}
			if ((!item) || (options.messageType !== tcConstants.COMPONENT_MESSAGE_TYPE.ERROR)) {
				options.keepExisting = false;
			}
			var hasConfirmAction = (angular.isString(options.confirmAction) && (options.confirmAction !== ''));
			if (hasConfirmAction && $scope.viewModel.processEventOptions.overrideWarning) {
				return true;
			}

			$scope.updateMessages([{
					messageType: options.messageType,
					messageText: options.messageText,
					messageField: item
				}], undefined, undefined, undefined, options.keepExisting);

			if (hasConfirmAction) {
				setConfirmAction(options.confirmAction, 'websys_setMessage');
				return false;
			}
			return true;
		};

		// set's viewModel properties so that the confirm button for W/I messages
		// will re-call processEvent for the correct item
		// only needed when messages aren't returned as part of the component data
		// (e.g. via websys_setMessage or tkMakeServerCall)
		// returns true if the confirmAction is valid, else returns false
		var setConfirmAction = function(confirmAction, source) {
			var actionItem = LegacyScopeSearchService.getItemFromDataObject($scope, confirmAction, true);
			if (!actionItem) {
				$log.warn($scope.componentName,
					': ' + source + ': Confirm action ', confirmAction, ' could not be found');
				return false;
			}
			$scope.viewModel.processEventOptions.processEventParams = {eventItemName: actionItem.id};
			return true;
		};

		/// component is TCUI embedded in MEUI
		var websysIsEmbedded = function() {
			return false;
		};

		/**
		* @ngdoc method
		* @name websys_toggleForm
		* @methodOf tcApp.WebsysLegacyScope
		* @description
		* Toggles or sets the visibility of the component form
		* @param {boolean} visibility [optional] Can be passed to manually set the form to
		*										 visible/hidden, otherwise the current state is toggled
		*/
		var websysToggleForm = function(visibility) {
			if (typeof visibility === 'boolean') {
				$scope.viewModel.hideForm = !visibility;
			} else {
				$scope.viewModel.hideForm = !$scope.viewModel.hideForm;
			}
			return;
		};

		/**
		* @ngdoc method
		* @name websys_getComponentName
		* @methodOf tcApp.WebsysLegacyScope
		* @description
		* Get current scope component name
		* @returns {string} The component name
		*/
		var websysGetComponentName = function() {
			return $scope.componentName;
		};

		/**
		* @ngdoc method
		* @name websys_getComponentHtmlName
		* @methodOf tcApp.WebsysLegacyScope
		* @description
		* Get current scope component html name
		* @returns {string} The component HTML name
		*/
		var websysGetComponentHtmlName = function() {
			return $scope.componentHtmlName;
		};

		/**
		* @ngdoc method
		* @name websys_formIsValid
		* @methodOf tcApp.WebsysLegacyScope
		* @description
		* Returns true if the current component's form is valid
		* @returns {boolean} Validity of the component form
		*/
		var websysFormIsValid = function() {
			return $scope.formIsValid();
		};
		
		/**
		* @ngdoc method
		* @name websys_selectTab
		* @methodOf tcApp.WebsysLegacyScope
		* @description
		* Manually selects a specified tab for a Tab Control item
		* @param {object} options object with properties:
		* @param {object} [options.item] Tab control item, either options.item or options.itemId must be passed.
		* 								 options.item will take precedence over options.itemId
		* @param {string} [options.itemId] Tab control item id. Will only be used if options.item is not passed.
		* @param {string} [options.tabName] Tab name to select, either options.tabName or options.tabIndex must be passed
		* @param {number} [options.tabIndex] Tab index to select (zero-indexed). If passed, will take precedence over options.tabName
		* @returns {boolean}  Returns true if tab selection did proceed (valid options are passed and tab could be found, etc.)
		*/
		var websysSelectTab = function(options) {
			if (angular.isDefined($scope.viewModel) && $scope.viewModel.tabSwitchingOnLoad) return false;
			var normalizedOptions = websysTabOptions('websys_selectTab', options);
			if (!normalizedOptions) return false;

			// Trigger tab selection via md-tabs md-selected binding
			$scope.data.items[normalizedOptions.item.id].tabData.selected = normalizedOptions.tabIndex;
			return true;
		};

		/**
		 * @ngdoc method
		 * @name websys_updateTab
		 * @methodOf tcApp.WebsysLegacyScope
		 * @description
		 * Updates a specified tab on a Tab Control item.
		 * @param {object} options - object with properties:
		 * @param {object} [options.item] - Tab control item, either options.item or options.itemId must be passed
		 * 									options.item will take precedence over options.itemId
		 * @param {string} [options.itemId] - Tab control item id. Will only be used if options.item is not passed.
		 * @param {string} [options.tabName] - Tab name to select, either options.tabName or options.tabIndex must be passed
		 * @param {number} [options.tabIndex] - Tab index to select (zero-indexed). If passed, will take precedence over options.tabName
		 * @param {string | number} [options.modifierText] - Text to set as the tab's modifier
		 * @param {string | number} [options.modifierTooltip] - Text to set as the tab modifier's tooltip
		 * @returns {boolean}  Returns true if valid options are passed, and tab control and tab could be found to update.
		 */
		var websysUpdateTab = function(options) {
			var normalizedOptions = websysTabOptions('websys_updateTab', options);
			if (!normalizedOptions) return false;

			var tabToUpdate = normalizedOptions.item.tabData.tabs[normalizedOptions.tabIndex];
			if (!tabToUpdate) return false;

			if (angular.isDefined(options.modifierText)) {
				tabToUpdate.modifierText = options.modifierText.toString();
			}
			if (angular.isDefined(options.modifierTooltip)) {
				tabToUpdate.modifierTooltip = options.modifierTooltip.toString();
			}

			return true;
		};
		
		/**
		 * @ngdoc method
		 * @name websysTabOptions
		 * @description
		 * Normalizes option parameters for tab control related functions (websys_selectTab, websys_updateTab)
		 * @param {string} functionName - Name of the originating function that called websysTabOptions
		 * @param {object} options - object with properties:
		 * @param {object} [options.item] - Tab control item, either options.item or options.itemId must be passed.
		 * 									options.item will take precedence over options.itemId
		 * @param {string} [options.itemId] - Tab control item id. Will only be used if options.item is not passed.
		 * @param {string} [options.tabName] - Tab name, either options.tabName or options.tabIndex must be passed
		 * @param {number} [options.tabIndex] - Tab index (zero-indexed). If passed, will take precedence over options.tabName
		 * @returns {object | null}  Returns the normalized options object with 'item' and 'tabIndex' properties
		 */
		var websysTabOptions = function(functionName, options) {
			if (!angular.isString(functionName)) return null;
			if (!angular.isObject(options)) return null;
			if (!angular.isObject(options.item) && (!angular.isString(options.itemId) || (options.itemId === ''))) {
				return null;
			}
			if ((!angular.isString(options.tabName) || (options.tabName === ''))
				&& !angular.isNumber(options.tabIndex)) {
				return null;
			}
			var item = (angular.isObject(options.item) ? options.item : websysGetItem(options.itemId));
			if (angular.isUndefined(item) || (item.displayType !== 'TAB') || (angular.isUndefined(item.tabData))
				|| (!angular.isArray(item.tabData.tabs))) {
				$log.error($scope.componentName,
					': ' + functionName + ': No item found, item is not a tab control, or tab data not found');
				return null;
			}

			var returnTabIndex = null;
			if (angular.isNumber(options.tabIndex)) {
				if ((options.tabIndex >= 0) && (options.tabIndex < item.tabData.tabs.length)) {
					returnTabIndex = options.tabIndex;
				}
			} else {
				angular.forEach(item.tabData.tabs, function(tab, tabIndex) {
					if (returnTabIndex !== null) return;
					if (tab.name === options.tabName) returnTabIndex = tabIndex;
				});
			}
			if (returnTabIndex === null) {
				$log.warn($scope.componentName, ': ' + functionName + ': Could not find tab with name \'' + options.tabName +'\''
					+ ' or index: ' + options.tabIndex);
				return null;
			}

			return {
				item: item,
				tabIndex: returnTabIndex,
			};
		};

		// Return all the functions in an object
		var websysFunctions = {
			'emptyFunction': emptyFunction,
			'buttonClickHandler': buttonClickHandler,
			'linkReturnHandler': linkReturnHandler,
			'tkMakeServerCall': tkMakeServerCall,
			'cspRunServerMethod': cspRunServerMethod,
			'StandardOnLoad': standardOnLoad,
			'RequiredMsgObj': requiredMsgObj,
			'mPiece': mPiece,

			/* websys.js functions */
			'websys_open': websysOpen,
			'websys_lu': websysOpen,
			'websys_printme': websysPrintMe,
			'websys_sckey': emptyFunction,
			'websys_getMainWindow': websysGetMainWindow,
			'websys_getSrcElement': websysGetSrcElement,
			'websys_getType': websysGetType,
			'websys_getKey': websysGetKey,
			'websys_getAlt': websysGetAlt,
			'websys_getShift': websysGetShift,
			'websys_getCtrl': websysGetCtrl,
			'websys_cancel': websysCancel,
			'websys_isDirty': websysIsDirty,
			'websys_setDirty': websysSetDirty,
			'websys_isDirtyPage': websysIsDirty,
			'websys_escape': websysEscape,
			'tkurlset': tkurlset,
			'websys_createWindow': websysCreateWindow,
			'websys_print': websysPrint,
			'websys_printlocal': websysPrintLocal,
			'websys_addListener': websysAddListener,
			'websys_removeListener': websysRemoveListener,
			'websys_setText': websysSetText,
			'websys_getText': websysGetText,
			'websys_setClass': websysSetClass,
			'websys_isClass': websysIsClass,
			'websys_fireEvent': websysFireEvent,
			'websys_fireMouseEvent': websysFireEvent,
			'websys_OpenInfo': websysCreateWindow,
			'websys_ToggleInfo': websysCreateWindow,
			'websys_DisplayNotification': websysDisplayNotification,
			'websys_DisplayWarnNotification': websysDisplayWarnNotification,
			'websys_setfocus': websysSetFocus,
			'websys_canfocus': websysCanFocus,

			'websys_getItem': websysGetItem,
			'websys_getValue': websysGetValue,
			'websys_getEditor': websysGetEditor,
			'websys_setValue': websysSetValue,
			'websys_isLocked': websysIsLocked,
			'websys_setInterval': websysSetInterval,
			'websys_clearInterval': websysClearInterval,
			'websys_setCaption': websysSetCaption,
			'websys_fetch': websysFetch,
			'websys_RefreshNotification': websysRefreshNotifications,
			'websys_getFullExternalUrl': websysGetFullExternalUrl,
			'websys_selectTab': websysSelectTab,
			'websys_updateTab': websysUpdateTab,

			/* websys.print.tools.js functions */
			'PassReportParametersForPreview': PassReportParametersForPreview,

			/* epr.menu.tools.js functions */
			'NavigateAwayAddCheck': navigateAwayAddCheck,
			'NavigateAwayCompleteCheck': navigateAwayCompleteCheck,

			/* Mobile Enabled UI specific functions */
			'websys_callFunction': websysCallFunction,
			'websys_reloadSession': websysReloadSession,
			'websys_removeCachedLayoutTemplate': websysRemoveCachedLayoutTemplate,
			'websys_RemoveAllComponentCache': websysRemoveAllComponentCache,
			'websys_printGetRequestParams': websysPrintGetRequestParams,
			'websys_replaceLocationParams': websysReplaceLocationParams,
			'websys_setReloadComponent': websysSetReloadComponent,
			'websys_setReturnValue': websysSetReturnValue,
			'websys_setExternalAuthUrl': websysSetExternalAuthUrl,
			'websys_setReloadRoute': websysSetReloadRoute,
			'websys_setComponentCaption': websysSetComponentCaption,
			'websys_setStatusClass': websysSetStatusClass,
			'websys_overlayClose': websysOverlayClose,
			'websys_loadNestedComponent': websysLoadNestedComponent,
			'websys_toggleTagged': websysToggleTagged,
			'websys_setMessage': websysSetMessage,
			'websys_toggleForm': websysToggleForm,
			'websys_getComponentName': websysGetComponentName,
			'websys_getComponentHtmlName': websysGetComponentHtmlName,
			'websys_formIsValid': websysFormIsValid,

			/* Not implemented - empty or stub functions */
			'treload': emptyFunction,
			'websys_getBrowser': websysGetBrowser,
			'websys_getBrowserVersion': websysGetBrowserVersion,
			'websys_getMainFrame': websysGetMainFrame,
			'websys_LoadContent': websysLoadContent,
			'websys_LoadIsLoading': emptyFunction,
			'websys_getAncestorByTagName': websysGetAncestorByTagName,
			'websys_getParentElement': websysGetParentElement,
			'websys_getChildElements': websysGetChildElements,
			'websys_getElementsByClassName': websysGetElementsByClassName,
			'websys_getPageCoords': websysGetPageCoords,
			'websys_SetTarget': emptyFunction,
			'websys_firstfocus': emptyFunction,
			'websys_nextfocus': emptyFunction,
			'websys_nextfocusElement': emptyFunction,
			'websys_nexttab': emptyFunction,
			'websys_firsterrorfocus': emptyFunction,
			'websys_reSize': emptyFunction,
			'websys_reSizeT': emptyFunction,
			'websys_move': emptyFunction,
			'tkurlget': tkurlget,
			'tkurlhref': tkurlhref,
			'websys_closeWindows': emptyFunction,
			'websys_onunload': emptyFunction,
			'websys_cleanWhitespace': emptyFunction,
			'websys_removeNode': emptyFunction,
			'websys_getBgColor': websysGetBgColor,
			'websys_isDocumentReady': websysIsDocumentReady,
			'websys_Overlay': websysOverlay,
			'websys_ToggleAccordion': emptyFunction,
			'websys_OpenAccordion': emptyFunction,
			'websys_CloseAccordion': emptyFunction,
			'websys_IsInAccordion': websysIsInAccordion,
			'websys_CloseInfo': emptyFunction,
			'websys_FindInfoPaneOpener': websysFindInfoPaneOpener,
			'websys_inInfoPane': websysInInfoPane,
			'PassReportParameters': emptyFunction,
			'CheckRequiredActiveXLoaded': checkRequiredActiveXLoaded,
			'CriticalCheckActiveX': checkRequiredActiveXLoaded,
			'websys_isEmbedded': websysIsEmbedded,
		};

		return websysFunctions;
	};

});
